import { toFactor, toSignificant, toPercentage } from '@/utilities/formatting';
import DataTable from '@components/DataTable';
import { SWS_ANALYSIS_MODEL } from '@/constants';

export interface ValuationPEGRatioProps {
  uniqueSymbol: string;
  peValue?: number;
  pegValue?: number;
  analystCount: number;
  futureNetIncomeGrowthAnnual: number | null;
  industryCountryName: string;
  industrySecondaryName: string;
  industryAveragePEGCount: number;
  industryAveragePEG: number;
  marketCountryName: string;
  marketAveragePEGCount: number;
  marketAveragePEG: number;
}

const ValuationPEGRatio = ({
  uniqueSymbol,
  peValue,
  pegValue,
  analystCount,
  futureNetIncomeGrowthAnnual,
  industryCountryName,
  industrySecondaryName,
  industryAveragePEGCount,
  industryAveragePEG,
  marketCountryName,
  marketAveragePEGCount,
  marketAveragePEG,
}: ValuationPEGRatioProps) => {
  return (
    <div>
      <DataTable columnWidths="4, 4, 2" light>
        <caption>{`${uniqueSymbol} PEG (Price to Earnings to Growth) Ratio Data Sources`}</caption>
        <thead>
          <tr>
            <th>Data Point</th>
            <th>Source</th>
            <th>Value</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>PE Ratio</td>
            <td>See PE Ratio Section</td>
            <td>{peValue ? `${toFactor(peValue, 2)}` : 'Not Available'}</td>
          </tr>
          <tr>
            <td>Net Income Annual Growth Rate</td>
            <td>
              <span>See Future Growth Section.</span>
              <br />
              <span>Line of best fit</span>
              <sup>*</sup>
              <span>{` through Consensus Estimate Earnings of ${toSignificant(
                analystCount,
                0
              )} Analysts`}</span>
            </td>
            <td>
              {futureNetIncomeGrowthAnnual
                ? `${toPercentage(futureNetIncomeGrowthAnnual, 1)} per year`
                : 'Not Available'}
            </td>
          </tr>
          {industryAveragePEG && (
            <tr>
              <td>
                {`${industryCountryName} ${industrySecondaryName} Industry PEG Ratio`}
              </td>
              <td>
                {`Median Figure of ${toSignificant(
                  industryAveragePEGCount
                )} Publicly-Listed ${industrySecondaryName} Companies`}
              </td>
              <td>{toFactor(industryAveragePEG, 2)}</td>
            </tr>
          )}
          {marketAveragePEG && (
            <tr>
              <td>{`${marketCountryName} Market PEG Ratio`}</td>
              <td>
                {`Median Figure of ${toSignificant(
                  marketAveragePEGCount
                )} Publicly-Listed Companies`}
              </td>
              <td>{toFactor(marketAveragePEG, 2)}</td>
            </tr>
          )}
        </tbody>
      </DataTable>
      <p>
        <sup>*</sup>
        <span>Line of best fit is calculated by </span>
        <a
          href={`${SWS_ANALYSIS_MODEL}/MODEL.markdown#future-performance`}
          data-unique-symbol={uniqueSymbol}
          data-about-section="valuation_peg_ratio"
          data-link-name="model_linear_regression"
          target="_blank"
          rel="noopener noreferrer nofollow"
        >
          linear regression.
        </a>
      </p>
      {pegValue && (
        <DataTable columnWidths="4, 4, 2" light>
          <caption>
            {`${uniqueSymbol} PEG (Price to Earnings to Growth) Ratio Calculation`}
          </caption>
          <thead>
            <tr>
              <th />
              <th>Calculation</th>
              <th>Outcome</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>PEG Ratio</td>
              <td>
                <p>= PE Ratio ÷ Net Income Annual Growth Rate</p>
                <p>
                  {`= ${toFactor(peValue as number, 2)} ÷ ${toPercentage(
                    futureNetIncomeGrowthAnnual as number,
                    1
                  )}`}
                </p>
              </td>
              <td>{toFactor(pegValue, 2)}</td>
            </tr>
          </tbody>
        </DataTable>
      )}
      <p>
        <span>
          {`Learn more about our ratios and growth rates in Simply Wall St’s `}
        </span>
        <a
          href={`${SWS_ANALYSIS_MODEL}/MODEL.markdown`}
          data-unique-symbol={uniqueSymbol}
          data-about-section="valuation_peg_ratio"
          data-link-name="model_growth_rates"
          target="_blank"
          rel="noopener noreferrer nofollow"
        >
          analysis model.
        </a>
      </p>
    </div>
  );
};

export default ValuationPEGRatio;
